// Dynamic import to keep mammoth out of the initial bundle and avoid SSR issues.
type MammothModule = {
  extractRawText: (options: { arrayBuffer: ArrayBuffer }) => Promise<{ value: string }>
  convertToHtml: (options: { arrayBuffer: ArrayBuffer }) => Promise<{ value: string }>
}

async function loadMammoth(): Promise<MammothModule> {
  const mammoth = await import('mammoth')
  return mammoth as MammothModule
}

type StructuredTextStats = {
  headingCount: number
  listItemCount: number
  paragraphCount: number
}

type StructuredTextResult = {
  text: string
  stats: StructuredTextStats
}

function createEmptyStats(): StructuredTextStats {
  return { headingCount: 0, listItemCount: 0, paragraphCount: 0 }
}

/**
 * Convert Mammoth HTML output into Dyslexa's structured text format.
 * Uses browser DOM APIs when available, otherwise falls back to a naive tag-stripper.
 */
export function convertHtmlToStructuredText(html: string): StructuredTextResult {
  const stats = createEmptyStats()

  if (!html.trim()) {
    return { text: '', stats }
  }

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    // Fallback for non-browser environments: strip tags and normalize whitespace.
    const plain = html.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, ' ')
    const normalized = plain
      .split('\n')
      .map(line => line.trimEnd())
      .join('\n')
      .trimEnd()
    return { text: normalized, stats }
  }

  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const lines: string[] = []

  const appendLine = (line: string) => {
    lines.push(line)
  }

  const textWithLineBreaks = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return (node.textContent ?? '')
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement
      if (el.tagName === 'BR') {
        return '\n'
      }
      let result = ''
      el.childNodes.forEach(child => {
        result += textWithLineBreaks(child)
      })
      return result
    }
    return ''
  }

  const processParagraphLike = (text: string) => {
    const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    const parts = normalized.split('\n')
    let hadContent = false

    parts.forEach(part => {
      const trimmed = part.trim()
      if (trimmed.length === 0) {
        appendLine('')
      } else {
        appendLine(trimmed)
        hadContent = true
      }
    })

    if (hadContent) {
      stats.paragraphCount += 1
    }
  }

  const walk = (node: Node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return
    }

    const el = node as HTMLElement
    const tag = el.tagName.toUpperCase()

    // Page/section breaks by class/style convention
    const className = el.className || ''
    const styleAttr = el.getAttribute('style') || ''
    const isPageBreak =
      /page-break/i.test(className) ||
      /page-break-before\s*:\s*always/i.test(styleAttr)

    if (isPageBreak) {
      appendLine('<STRONA>')
      return
    }

    if (tag === 'H1' || tag === 'H2' || tag === 'H3' || tag === 'H4') {
      const text = el.textContent?.trim()
      if (text) {
        if (tag === 'H1' || tag === 'H2') {
          appendLine(`## ${text} ##`)
        } else {
          appendLine(`### ${text} ###`)
        }
        appendLine('')
        stats.headingCount += 1
      }
      return
    }

    if (tag === 'P') {
      const text = textWithLineBreaks(el).trimEnd()
      if (!text && !el.querySelector('br')) {
        // Explicit empty paragraph -> blank line
        appendLine('')
      } else if (text) {
        processParagraphLike(text)
      }
      return
    }

    if (tag === 'UL' || tag === 'OL') {
      const items = Array.from(el.querySelectorAll('li'))
      items.forEach(li => {
        const raw = textWithLineBreaks(li).replace(/\s+/g, ' ').trim()
        if (raw) {
          appendLine(`* ${raw}`)
          stats.listItemCount += 1
        }
      })
      appendLine('')
      return
    }

    if (tag === 'LI') {
      const raw = textWithLineBreaks(el).replace(/\s+/g, ' ').trim()
      if (raw) {
        appendLine(`* ${raw}`)
        stats.listItemCount += 1
      }
      return
    }

    if (tag === 'IMG') {
      const alt = el.getAttribute('alt') || 'Obraz'
      appendLine(`[OBRAZ] ${alt} [/OBRAZ]`)
      appendLine('')
      return
    }

    // Default: recurse into children
    el.childNodes.forEach(child => walk(child))
  }

  doc.body.childNodes.forEach(child => walk(child))

  const joined = lines
    .join('\n')
    .replace(/[ \t]+\n/g, '\n') // trim trailing spaces per line
    .replace(/\n{3,}/g, '\n\n') // collapse 3+ blank lines to 2
    .trimEnd()

  return { text: joined, stats }
}

/**
 * Imports a DOCX (Word) file and extracts structured text.
 * Prefers HTML-based import to preserve headings, lists and paragraphs,
 * with a raw-text fallback for robustness.
 */
export async function importDocxToText(
  file: File,
  logger?: { info: (message: string, context?: Record<string, unknown>) => void; error?: (message: string, context?: Record<string, unknown>) => void }
): Promise<string> {
  if (!file) {
    throw new Error('Brak pliku do wczytania.')
  }

  const fileName = file.name || 'unknown.docx'

  try {
    const arrayBuffer = await file.arrayBuffer()
    const mammoth = await loadMammoth()

    let strategy: 'html' | 'raw' = 'html'
    let structuredText = ''
    let stats: StructuredTextStats = createEmptyStats()

    try {
      const htmlResult = await mammoth.convertToHtml({ arrayBuffer })
      const html = typeof htmlResult?.value === 'string' ? htmlResult.value : ''
      if (html.trim()) {
        const result = convertHtmlToStructuredText(html)
        structuredText = result.text
        stats = result.stats
      }
    } catch {
      strategy = 'raw'
    }

    if (!structuredText.trim()) {
      strategy = 'raw'
      const rawResult = await mammoth.extractRawText({ arrayBuffer })
      const rawText: string = typeof rawResult?.value === 'string' ? rawResult.value : ''
      structuredText = rawText.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim()
      stats = createEmptyStats()
    }

    if (!structuredText.trim()) {
      throw new Error('Nie udało się odczytać tekstu z dokumentu Word (.docx).')
    }

    logger?.info('DOCX text extracted', {
      fileName,
      length: structuredText.length,
      strategy,
      headingCount: stats.headingCount,
      listItemCount: stats.listItemCount,
      paragraphCount: stats.paragraphCount,
    })

    return structuredText
  } catch (error) {
    logger?.error?.('DOCX import failed', {
      fileName,
      error: error instanceof Error ? error.message : String(error),
    })
    throw new Error('Nie udało się wczytać dokumentu Word (.docx). Upewnij się, że plik nie jest uszkodzony.')
  }
}

